// ============================================================================
// ui/product-info.js — what each product IS, and whose it is
// ============================================================================
// Feeds the gate's product detail bar (ui/product-detail-bar.js): hover or pick a
// pill and this is what gets written into the bottom of the stage.
//
// Replaces the flat `${cat}|${kind} -> string` blurb map (2026-07-15). That shape
// could only ever say ONE thing about a product, which was fine while every
// product was ours. Ryan: "let's say we eventually add other brands to this tool
// (UMAX, Troy Barbell, etc.) — this would accommodate that much better than the
// tooltips, which can only hold so much information and no logo." He's right, and
// the logo is the point: once the tool sells more than one line, WHOSE a product
// is becomes part of the buying decision, and a tooltip fundamentally cannot carry
// a mark. So a product has a BRAND, and brands are their own registry — adding
// UMAX later is adding a record in here, not touching a component.
//
// ⚠ THE BRAND NAME IS PROVISIONAL. Ryan, 2026-07-15: "our in-house brand will
// likely be Ballast Strength or something like that (still figuring it out)."
// It's data precisely so that's a one-line change when he settles it.
// ✅ LOGO ART LANDED 2026-07-29 (Ballast's real mark is on BRANDS.ballast below).
// `logo: null` is still what the bar reads to draw the placeholder plate, so any
// NEW brand added here starts null and needs nothing but a URL later.
//
// ⚠ COPY STATUS — deliberately mixed, and the difference matters:
//   grip|Premium Urethane (2026-07-15) and bumper|Training Rubber (2026-08-07) —
//     RYAN'S OWN LINES, verbatim. They name real specifics (three hand grips, a
//     stainless steel hub; "rather than Olympic lifting competitions") BECAUSE
//     they came from him. This is the format every other entry should end up in.
//   everything else — RYAN HAS READ THEM AND SAID THEY READ WELL (2026-08-07), but
//     they are still inferred rather than sourced. Carried over from the old blurb
//     map, where each line described only the distinction the product NAME already
//     states: the material (urethane vs rubber) and the format (competition vs
//     training, straight vs EZ-curl), using general, industry-standard properties
//     of those materials. They carry NO Blue Sky spec claims: no weight tolerances,
//     no certifications, no warranty terms, no dimensions. Those are real numbers
//     on real SKUs and this file is not where they get invented. Replace each with
//     Ryan's own words as they arrive.
//
// ⛔ NO EM DASHES IN A BLURB. Ryan, 2026-08-07: "way too many em dashes. That
// always reads as AI." Every blurb was rebuilt on 2026-08-07 to use full stops and
// commas instead. This applies to the SHIPPED STRINGS only — these comments are
// full of them and that is fine, nobody reads a buyer-facing surface here. When you
// add a product, write two plain sentences. `tools/verify-blurb-copy.mjs` fails the
// build's sibling check if an em dash (or an en dash used as one) creeps back in.
// ============================================================================

import { BRAND_PUBLIC, BRAND_NAME } from '../brand-flag.js';

/**
 * Every brand whose product can appear in the tool. `logo` is a URL or null —
 * null draws the placeholder plate rather than a broken image.
 */
export const BRANDS = Object.freeze({
  /* The storefront itself, and what every product resolves to while the house line
     is unannounced (brand-flag.js). It is a real brand record rather than a "no
     brand" branch on purpose: the detail plate is BUILT around a mark to the left
     of a name, and Ryan rejected a second layout for this space back on 2026-07-15
     because switching between layouts made the plate jump as you moused across the
     pills. Dropping the mark would have re-created exactly that. This keeps one
     layout, always populated.
     ⚠ THE PRIMARY (DARK INK) MARK. Ryan, 2026-08-02: "we used the wrong Blue Sky
     Fitness Supply logo — use the primary one." This is the Brand-Kit "dark logo,
     for light backgrounds" URL.
     ⚠ THE REASON IT IS RIGHT CHANGED ON 2026-08-07, so don't re-derive it from the
     old one. It used to be "pick the variant from the TILE, not the stage" — the
     mark sat on a LIGHT `.wd-pdb__logo` tile (hairline + soft shadow) even though
     Ballast's comment below calls every surface in this tool GD-dark. Ryan has
     since removed the tile: the PNG now sits directly on the gate backdrop. That
     backdrop is `--dt-color-stage-bg` #e5e8ed under white/cool washes — still
     LIGHT — so the answer holds and the question is once again about the STAGE.
     Ballast's note below is about the 3D/canvas surfaces, not this plate. */
  bluesky: Object.freeze({
    id: 'bluesky',
    name: 'Blue Sky Fitness Supply',
    logo: 'https://cdn.shopify.com/s/files/1/0684/9350/8844/files/Primary_PNG_8eedc73e-47f6-4e3b-aeda-53c647e961b7.png?v=1734676443',
  }),
  /* SPREAD, not a plain key, so the record LEAVES THE SHIPPED BUNDLE while the
     brand is hidden. `BRAND_PUBLIC` is an imported const, which the bundler folds
     at build time and then drops the dead branch — taking the name string and the
     logo URL with it. Without this the tool renders nothing branded but still
     ships "Ballast Strength" and a Ballast CDN path in plain sight to anyone who
     opens the JS, which is a reference like any other.
     What stays is the lowercase `brand: 'ballast'` slug on the PRODUCT_INFO rows.
     That is deliberate — it is the row's own record of whose the product is, it
     names nothing on its own, and keeping it is what makes re-enabling one line. */
  ...(BRAND_PUBLIC ? {
  ballast: Object.freeze({
    id: 'ballast',
    name: BRAND_NAME,
    /* The WHITE-on-transparent mark, and that is the whole reason this one and not the other.
       Every surface it lands on in this tool is GD-dark — the product detail plate sits on the
       #000F1C stage — so the black-on-transparent primary would render as a hole. Its sibling
       lives at …Ballast-Strength-Main-Logo-Primary-No-Background.webp?v=1785363019 and is the
       right pick for any BRIGHT surface (a printed sheet, a light email body); neither is a
       general default. Art arrived 2026-07-29 from the Ballast Strength catalog session, which
       replaces the "no art yet" placeholder that used to be on this line. */
    logo: 'https://cdn.shopify.com/s/files/1/0684/9350/8844/files/Ballast-Strength-Main-Logo-Secondary-No-Background.webp?v=1785363022',
  }),
  } : {}),
});

/** What a product falls back to when it doesn't name a brand. */
export const DEFAULT_BRAND = BRAND_PUBLIC ? BRANDS.ballast : BRANDS.bluesky;

/**
 * Keyed `${categoryId}|${kind}` — the same pair the pills are built from, and the
 * same keys the old blurb map used.
 */
export const PRODUCT_INFO = Object.freeze({
  'bumper|Competition Urethane': { brand: 'ballast', blurb: 'Competition profile with a urethane skin. The hardest wearing finish, and it stays odor free.' },
  'bumper|Competition Rubber': { brand: 'ballast', blurb: 'The competition profile in rubber. Same shape, friendlier price.' },
  // RYAN'S OWN LINE, 2026-08-07 — verbatim but for "everday" → "everyday".
  'bumper|Training Rubber': { brand: 'ballast', blurb: 'Built for everyday training volume rather than Olympic lifting competitions. Thick, affordable, and long lasting.' },
  // Ryan's own words, 2026-07-15 — the one entry sourced rather than inferred.
  // "rubber coated" CORRECTED to "urethane coated" 2026-07-29: the supplier price
  // list carries exactly one grip plate and it is polyurethane (section "PU Grip
  // Plate", SKUs PUGP2.5-PUGP45, Blue Sky product name "Urethane Coated Grip
  // Plate"). The rest of the sentence is still verbatim Ryan.
  'grip|Premium Urethane': { brand: 'ballast', blurb: 'High-end commercial grade urethane coated plate with three hand grips and a stainless steel hub.' },
  'dumbbell|Round Urethane': { brand: 'ballast', blurb: 'Round heads in urethane, the commercial gym finish. Hardest wearing, and it stays odor free.' },
  // dumbbell|Premium Rubber is ABSENT — its pill is pulled from CATEGORIES until
  // Ryan finishes modelling it (2026-07-15). Its blurb was "Our upgraded rubber
  // dumbbell — a step up in finish from the standard round."; restore it here
  // alongside the kind + a products.js row when the model lands.
  'dumbbell|Round Rubber': { brand: 'ballast', blurb: 'The classic round rubber dumbbell. The most affordable of the three.' },
  'barbell|Straight Bar Urethane': { brand: 'ballast', blurb: 'Fixed straight bar with urethane ends. Built for presses, rows, and everything on a straight line.' },
  'barbell|EZ Curl Bar Urethane': { brand: 'ballast', blurb: 'Fixed EZ-curl bar with urethane ends. The angled grip is kinder on wrists and elbows.' },
  'barbell|Straight Bar Rubber': { brand: 'ballast', blurb: 'Fixed straight bar with rubber ends. The same bar at a friendlier price.' },
  'barbell|EZ Curl Bar Rubber': { brand: 'ballast', blurb: 'Fixed EZ-curl bar with rubber ends. The same angled grip at a friendlier price.' },
});

/**
 * The full record for a {category, kind}: its blurb and its resolved brand.
 * @returns {{blurb: string, brand: {id: string, name: string, logo: ?string}}|null}
 *   null when nothing is written for that product yet — callers must handle it,
 *   since a new SKU lands in CATEGORIES before its copy does.
 */
export function infoFor(categoryId, kind) {
  const rec = PRODUCT_INFO[`${categoryId}|${kind}`];
  if (!rec) return null;
  // While the house line is unannounced, EVERY product resolves to the storefront
  // brand regardless of what its row says. The rows keep their real `brand` key —
  // what the flag changes is RESOLUTION, not the data — so the day the Ballast
  // pages go live, one boolean gives all eleven of them their real mark back.
  const brand = BRAND_PUBLIC ? (BRANDS[rec.brand] || DEFAULT_BRAND) : DEFAULT_BRAND;
  return { blurb: rec.blurb, brand };
}

/** Just the one line, for callers that don't care whose it is (the pills' a11y description). */
export function blurbFor(categoryId, kind) {
  return infoFor(categoryId, kind)?.blurb || '';
}
